/**
 * BST Header — Mega menu, mobile accordion nav, predictive search
 */

(function () {
  'use strict';

  const OPEN_CLASS = 'is-open';
  const ACTIVE_CLASS = 'bst-header__sub-item--active';
  const HOVER_DELAY = 120;
  const DESKTOP_MQ = window.matchMedia('(min-width: 990px)');

  class BstHeader {
    constructor(el) {
      this.header = el;
      this.navItems = el.querySelectorAll('.bst-header__nav-item--has-mega');
      this.dropdownItems = el.querySelectorAll('.bst-header__nav-item--has-dropdown');
      this.panels = el.querySelectorAll('.bst-header__mega-panel');
      this.overlay = el.querySelector('.bst-header__overlay');
      this.mobileToggle = el.querySelector('.bst-header__mobile-toggle');
      this.mobileMenu = el.querySelector('.bst-header__mobile-menu');

      this._activePanel = null;
      this._activeDropdown = null;
      this._closeTimer = null;
      this._previouslyFocused = null;
      this._hoveredNavLink = null;

      this.indicator = el.querySelector('.bst-header__nav-indicator');
      this.nav = el.querySelector('.bst-header__nav');
      this._stickyEnabled = el.classList.contains('bst-header--sticky-enabled');
      this._sectionHeader = el.closest('.section-header');
      this._megaPositionHandler = () => {
        if (this._activePanel) this._updateMegaPanelPosition();
        if (this.mobileMenu?.classList.contains(OPEN_CLASS)) this._updateMobileLayoutPosition();
      };
      this._mobileLayoutHandler = () => {
        if (this.mobileMenu?.classList.contains(OPEN_CLASS)) this._updateMobileLayoutPosition();
      };
      this._bodyScrollLocked = false;
      this._mobileScrollLocked = false;
      this._overlayDismissReady = true;
      this._mobileMenuPortalParent = null;
      this._overlayPortalParent = null;
      this._mobileMenuPortalNext = null;
      this._overlayPortalNext = null;

      this._bindDesktop();
      this._bindNavIndicator();
      this._bindMobile();
      this._bindMobileSubItems();
      this._bindKeyboard();
      this._bindResize();
    }

    _bindDesktop() {
      if (this._stickyEnabled) {
        window.addEventListener('scroll', this._megaPositionHandler, { passive: true });
        window.addEventListener('resize', this._megaPositionHandler, { passive: true });
      }

      this.navItems.forEach((item) => {
        const handle = item.dataset.navHandle;
        const panel = this.header.querySelector(`.bst-header__mega-panel[data-nav-handle="${handle}"]`);
        if (!panel) return;

        item.addEventListener('mouseenter', () => {
          clearTimeout(this._closeTimer);
          this._openPanel(item, panel);
        });

        item.addEventListener('mouseleave', () => {
          this._scheduleClose(item, panel);
        });

        panel.addEventListener('mouseenter', () => {
          clearTimeout(this._closeTimer);
        });

        panel.addEventListener('mouseleave', () => {
          this._scheduleClose(item, panel);
        });
      });

      document.addEventListener('click', (e) => {
        if (this.header.contains(e.target)) return;
        if (this.mobileMenu?.contains(e.target)) return;
        if (this.overlay?.contains(e.target)) return;
        this._closeAll();
      });

      this.panels.forEach((panel) => {
        this._bindSubItems(panel);
      });

      this.dropdownItems.forEach((item) => {
        const dropdown = item.querySelector('.bst-header__dropdown');
        if (!dropdown) return;

        item.addEventListener('mouseenter', () => {
          clearTimeout(this._closeTimer);
          this._openDropdown(item);
        });

        item.addEventListener('mouseleave', () => {
          this._scheduleCloseDropdown(item);
        });

        dropdown.addEventListener('mouseenter', () => {
          clearTimeout(this._closeTimer);
        });

        dropdown.addEventListener('mouseleave', () => {
          this._scheduleCloseDropdown(item);
        });
      });
    }

    closeAllMenus() {
      this._closeAll();
      this._closeMobile();
    }

    _closePredictiveSearch() {
      this.header.querySelectorAll('predictive-search[open]').forEach((el) => {
        if (typeof el.close === 'function') el.close();
      });
    }

    _getAnnouncementBarHeight() {
      const announcement = document.querySelector('.bst-announcement-bar');
      if (!announcement) return 0;
      const section = announcement.closest('.bst-announcement-bar-section');
      const el = section || announcement;
      return Math.ceil(el.getBoundingClientRect().height);
    }

    _updateMegaPanelPosition() {
      const bar = this.header.querySelector('.bst-header__bar');
      if (!bar) return;
      const bottom = Math.ceil(bar.getBoundingClientRect().bottom);
      this.header.style.setProperty('--bst-mega-panel-top', `${bottom}px`);
      this.header.style.setProperty('--bst-mega-overlay-top', `${bottom}px`);
    }

    _updateMobileLayoutPosition() {
      const bar = this.header.querySelector('.bst-header__bar');
      if (!bar) return;
      const rect = bar.getBoundingClientRect();
      const bottom = Math.ceil(rect.bottom);
      const top = Math.ceil(rect.top);
      document.documentElement.style.setProperty('--header-bottom-position', `${bottom}px`);
      document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      document.documentElement.style.setProperty('--bst-mobile-menu-top', `${bottom}px`);
      document.documentElement.style.setProperty('--bst-mobile-bar-top', `${top}px`);
    }

    _clearMobileLayoutPosition() {
      document.documentElement.style.removeProperty('--header-bottom-position');
      document.documentElement.style.removeProperty('--bst-mobile-menu-top');
      document.documentElement.style.removeProperty('--bst-mobile-bar-top');
    }

    _portalMobileMenu(active) {
      if (!this.mobileMenu || !this.overlay) return;

      if (active) {
        if (!this._mobileMenuPortalParent) {
          this._mobileMenuPortalParent = this.mobileMenu.parentNode;
          this._overlayPortalParent = this.overlay.parentNode;
          this._mobileMenuPortalNext = this.mobileMenu.nextSibling;
          this._overlayPortalNext = this.overlay.nextSibling;
        }
        document.body.appendChild(this.overlay);
        document.body.appendChild(this.mobileMenu);
        return;
      }

      if (!this._mobileMenuPortalParent || !this._overlayPortalParent) return;

      this._overlayPortalParent.insertBefore(this.overlay, this._overlayPortalNext);
      this._mobileMenuPortalParent.insertBefore(this.mobileMenu, this._mobileMenuPortalNext);
    }

    _lockBodyScroll({ mobile = false } = {}) {
      if (this._bodyScrollLocked) return;

      document.documentElement.classList.add('bst-header-scroll-locked');
      document.body.classList.add(mobile ? 'overflow-hidden' : 'overflow-hidden-tablet');
      this._bodyScrollLocked = true;
      this._mobileScrollLocked = mobile;

      if (mobile) {
        this._preventTouchMove =
          this._preventTouchMove ||
          ((e) => {
            if (this.mobileMenu?.contains(e.target)) return;
            e.preventDefault();
          });
        document.addEventListener('touchmove', this._preventTouchMove, { passive: false });
      }
    }

    _unlockBodyScroll() {
      if (!this._bodyScrollLocked) return;

      document.documentElement.classList.remove('bst-header-scroll-locked');
      document.body.classList.remove('overflow-hidden', 'overflow-hidden-tablet');
      this._bodyScrollLocked = false;

      if (this._mobileScrollLocked && this._preventTouchMove) {
        document.removeEventListener('touchmove', this._preventTouchMove);
      }

      this._mobileScrollLocked = false;
    }

    _showOverlay({ mega = false } = {}) {
      if (!this.overlay) return;

      if (mega) {
        this._updateMegaPanelPosition();
        this.overlay.classList.add('is-mega');
        document.documentElement.classList.add('bst-mega-open');
        this.header.classList.add('bst-header--mega-open');
      } else {
        this.overlay.classList.remove('is-mega');
        document.documentElement.classList.remove('bst-mega-open');
      }

      this.overlay.style.display = 'block';
      void this.overlay.offsetHeight;
      this.overlay.classList.add('is-visible');

      // Lock page scroll while overlay is open
      if (!mega || !DESKTOP_MQ.matches) {
        this._lockBodyScroll({ mobile: !mega && !DESKTOP_MQ.matches });
      }
    }

    _hideOverlay() {
      if (!this.overlay) return;

      const mobileOpen = this.mobileMenu?.classList.contains(OPEN_CLASS);
      const megaOpen = Boolean(this._activePanel);

      if (mobileOpen || megaOpen) return;

      const wasMega = this.overlay.classList.contains('is-mega');

      this.overlay.classList.remove('is-visible');
      document.documentElement.classList.remove('bst-mega-open');

      if (wasMega) {
        this.overlay.classList.remove('is-mega');
        this.header.classList.remove('bst-header--mega-open');
        this.header.style.removeProperty('--bst-mega-overlay-top');
        this.header.style.removeProperty('--bst-mega-panel-top');
        this.overlay.style.display = '';
        this._unlockBodyScroll();
        return;
      }

      setTimeout(() => {
        if (!this.overlay.classList.contains('is-visible')) {
          this.overlay.style.display = '';
        }
      }, 200);
      this._unlockBodyScroll();
    }

    _openPanel(item, panel) {
      this._closePredictiveSearch();
      this._closeAllDropdowns();

      if (this._activePanel && this._activePanel !== panel) {
        this._closePanel(
          this.header.querySelector(`.bst-header__nav-item--has-mega[data-nav-handle="${this._activePanel.dataset.navHandle}"]`),
          this._activePanel,
          { hideOverlay: false }
        );
      }
      item.classList.add(OPEN_CLASS);
      panel.classList.add(OPEN_CLASS);
      panel.setAttribute('aria-hidden', 'false');
      item.querySelector('.bst-header__nav-link')?.setAttribute('aria-expanded', 'true');
      this._activePanel = panel;
      this._moveNavIndicator(item.querySelector('.bst-header__nav-link'));
      this._showOverlay({ mega: true });
    }

    _closePanel(item, panel, { hideOverlay = true } = {}) {
      if (!panel) return;
      item?.classList.remove(OPEN_CLASS);
      panel.classList.remove(OPEN_CLASS);
      panel.setAttribute('aria-hidden', 'true');
      item?.querySelector('.bst-header__nav-link')?.setAttribute('aria-expanded', 'false');
      if (this._activePanel === panel) this._activePanel = null;
      this._resetNavIndicator();
      if (hideOverlay) this._hideOverlay();
    }

    _scheduleClose(item, panel) {
      this._closeTimer = setTimeout(() => {
        this._closePanel(item, panel);
      }, HOVER_DELAY);
    }

    _closeAll() {
      this.navItems.forEach((item) => {
        const handle = item.dataset.navHandle;
        const panel = this.header.querySelector(`.bst-header__mega-panel[data-nav-handle="${handle}"]`);
        this._closePanel(item, panel);
      });
      this._closeAllDropdowns();
    }

    _openDropdown(item) {
      this._closePredictiveSearch();
      this._closeAllDropdowns();

      this.navItems.forEach((navItem) => {
        const handle = navItem.dataset.navHandle;
        const megaPanel = this.header.querySelector(`.bst-header__mega-panel[data-nav-handle="${handle}"]`);
        this._closePanel(navItem, megaPanel, { hideOverlay: false });
      });
      this._hideOverlay();

      const dropdown = item.querySelector('.bst-header__dropdown');
      item.classList.add(OPEN_CLASS);
      dropdown?.setAttribute('aria-hidden', 'false');
      item.querySelector('.bst-header__nav-link')?.setAttribute('aria-expanded', 'true');
      this._activeDropdown = item;
      this._positionDropdown(item);
      this._moveNavIndicator(item.querySelector('.bst-header__nav-link'));
    }

    _positionDropdown(item) {
      const link = item?.querySelector('.bst-header__nav-link');
      const dropdown = item?.querySelector('.bst-header__dropdown');
      const bar = this.header.querySelector('.bst-header__bar');
      if (!link || !dropdown || !bar || !DESKTOP_MQ.matches) return;

      const headerRect = this.header.getBoundingClientRect();
      const barRect = bar.getBoundingClientRect();
      const linkRect = link.getBoundingClientRect();

      dropdown.style.left = `${linkRect.left - headerRect.left}px`;
      dropdown.style.top = `${barRect.bottom - headerRect.top}px`;
    }

    _resetDropdownPosition(dropdown) {
      if (!dropdown) return;
      dropdown.style.left = '';
      dropdown.style.top = '';
    }

    _closeDropdown(item) {
      if (!item) return;
      const dropdown = item.querySelector('.bst-header__dropdown');
      item.classList.remove(OPEN_CLASS);
      dropdown?.setAttribute('aria-hidden', 'true');
      this._resetDropdownPosition(dropdown);
      item.querySelector('.bst-header__nav-link')?.setAttribute('aria-expanded', 'false');
      if (this._activeDropdown === item) this._activeDropdown = null;
      this._resetNavIndicator();
    }

    _scheduleCloseDropdown(item) {
      this._closeTimer = setTimeout(() => {
        this._closeDropdown(item);
      }, HOVER_DELAY);
    }

    _closeAllDropdowns() {
      this.dropdownItems.forEach((item) => {
        this._closeDropdown(item);
      });
    }

    _getNavIndicatorLink() {
      if (!this.nav) return null;

      const openLink = this.nav.querySelector('.bst-header__nav-item.is-open .bst-header__nav-link');
      if (openLink) return openLink;

      return this.nav.querySelector('.bst-header__nav-link--active');
    }

    _getHoveredNavLink() {
      if (!this.nav) return null;
      return this.nav.querySelector('.bst-header__nav-link:hover');
    }

    _resetNavIndicator() {
      const hoveredLink = this._hoveredNavLink || this._getHoveredNavLink();
      if (hoveredLink) {
        this._moveNavIndicator(hoveredLink);
        return;
      }
      this._moveNavIndicator(this._getNavIndicatorLink());
    }

    _moveNavIndicator(link) {
      if (!this.indicator || !link || !DESKTOP_MQ.matches) {
        if (this.indicator) this.indicator.hidden = true;
        return;
      }

      const bar = this.header.querySelector('.bst-header__bar');
      if (!bar) return;

      const barRect = bar.getBoundingClientRect();
      const linkRect = link.getBoundingClientRect();

      this.indicator.hidden = false;
      this.indicator.style.left = `${linkRect.left - barRect.left}px`;
      this.indicator.style.width = `${linkRect.width}px`;
    }

    _bindNavIndicator() {
      if (!this.indicator || !this.nav) return;

      this._hoveredNavLink = null;

      this.nav.addEventListener('pointerover', (e) => {
        const link = e.target.closest('.bst-header__nav-link');
        if (!link || !this.nav.contains(link)) return;
        this._closePredictiveSearch();
        this._hoveredNavLink = link;
        this._moveNavIndicator(link);
      });

      this.nav.addEventListener('pointerout', (e) => {
        if (e.relatedTarget && this.nav.contains(e.relatedTarget)) return;
        this._hoveredNavLink = null;
        this._resetNavIndicator();
      });

      this.nav.addEventListener('focusin', (e) => {
        const link = e.target.closest('.bst-header__nav-link');
        if (!link || !this.nav.contains(link)) return;
        this._hoveredNavLink = link;
        this._moveNavIndicator(link);
      });

      this.nav.addEventListener('focusout', (e) => {
        if (this.nav.contains(e.relatedTarget)) return;
        this._hoveredNavLink = null;
        this._resetNavIndicator();
      });

      window.addEventListener('resize', () => this._resetNavIndicator());
      DESKTOP_MQ.addEventListener('change', () => this._resetNavIndicator());

      requestAnimationFrame(() => this._resetNavIndicator());
    }

    _bindSubItems(panel) {
      const subItems = panel.querySelectorAll('.bst-header__sub-item');
      const modelLists = panel.querySelectorAll('.bst-header__model-list');

      subItems.forEach((item) => {
        item.addEventListener('mouseenter', () => {
          const handle = item.dataset.subHandle;

          subItems.forEach((i) => i.classList.remove(ACTIVE_CLASS));
          item.classList.add(ACTIVE_CLASS);

          modelLists.forEach((list) => {
            const isActive = list.dataset.modelsFor === handle;
            list.classList.toggle('is-active', isActive);
            list.hidden = !isActive;
          });
        });
      });
    }

    _bindMobile() {
      if (!this.mobileToggle || !this.mobileMenu) return;

      this.mobileToggle.addEventListener('click', () => {
        if (this.mobileMenu.classList.contains(OPEN_CLASS)) {
          this._closeMobile();
        } else {
          this._openMobile();
        }
      });

      this.overlay?.addEventListener('click', () => {
        if (!this._overlayDismissReady) return;
        if (this.mobileMenu?.classList.contains(OPEN_CLASS)) {
          this._closeMobile();
        } else if (this._activePanel) {
          this._closeAll();
        } else if (this._activeDropdown) {
          this._closeAllDropdowns();
        }
      });
      this.mobileMenu?.addEventListener('keydown', (e) => this._handleMobileMenuKeydown(e));
    }

    _bindMobileSubItems() {
      this.header.querySelectorAll('[data-bst-mobile-sub-accordion]').forEach((item) => {
        const trigger = item.querySelector('[data-bst-mobile-sub-trigger]');
        const panel = item.querySelector('[data-bst-mobile-sub-panel]');
        if (!trigger || !panel) return;

        trigger.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();

          const open = !item.classList.contains(OPEN_CLASS);
          item.classList.toggle(OPEN_CLASS, open);
          trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
          panel.setAttribute('aria-hidden', open ? 'false' : 'true');
        });
      });
    }

    _getMobileFocusables() {
      if (!this.mobileMenu) return [];

      return Array.from(
        this.mobileMenu.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => {
        if (el.closest('[hidden]')) return false;
        if (el.getAttribute('aria-hidden') === 'true') return false;
        if (el.closest('[aria-hidden="true"]')) return false;
        return true;
      });
    }

    _handleMobileMenuKeydown(e) {
      if (!this.mobileMenu?.classList.contains(OPEN_CLASS) || e.key !== 'Tab') return;

      const focusables = this._getMobileFocusables();
      if (!focusables.length) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    _openMobile() {
      if (!DESKTOP_MQ.matches) {
        this._closePredictiveSearch();
        clearTimeout(this._closeTimer);
        this._closeAll();
        this._previouslyFocused = document.activeElement;
        this._overlayDismissReady = false;

        this._updateMobileLayoutPosition();
        this.header.classList.add('bst-header--mobile-open');
        this._sectionHeader?.classList.add('menu-open');
        this._portalMobileMenu(true);

        this.mobileMenu.classList.add(OPEN_CLASS);
        this._showOverlay({ mega: false });
        this._lockBodyScroll({ mobile: true });
        this.mobileToggle.setAttribute('aria-expanded', 'true');
        this.mobileMenu.setAttribute('aria-hidden', 'false');

        window.addEventListener('resize', this._mobileLayoutHandler, { passive: true });

        requestAnimationFrame(() => {
          this._updateMobileLayoutPosition();
          this.mobileToggle?.focus({ preventScroll: true });
          requestAnimationFrame(() => {
            this._overlayDismissReady = true;
          });
        });
        return;
      }

      this._closePredictiveSearch();
      clearTimeout(this._closeTimer);
      this._closeAll();
      this._previouslyFocused = document.activeElement;
      this.mobileMenu.classList.add(OPEN_CLASS);
      this._showOverlay({ mega: false });
      this.mobileToggle.setAttribute('aria-expanded', 'true');
      this.mobileMenu.setAttribute('aria-hidden', 'false');
    }

    _closeMobile() {
      if (!this.mobileMenu?.classList.contains(OPEN_CLASS)) return;

      this.mobileMenu.classList.remove(OPEN_CLASS);
      this.header.classList.remove('bst-header--mobile-open');
      this._sectionHeader?.classList.remove('menu-open');
      this._clearMobileLayoutPosition();
      this._portalMobileMenu(false);
      this._unlockBodyScroll();
      this._hideOverlay();
      this.mobileToggle?.setAttribute('aria-expanded', 'false');
      this.mobileMenu?.setAttribute('aria-hidden', 'true');
      this._overlayDismissReady = true;

      window.removeEventListener('resize', this._mobileLayoutHandler);

      const returnFocus = this._previouslyFocused || this.mobileToggle;
      this._previouslyFocused = null;
      returnFocus?.focus({ preventScroll: true });
    }

    _bindKeyboard() {
      this.header.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          this._closeAll();
          this._closeMobile();
          document.activeElement?.closest('.bst-header__nav-item')
            ?.querySelector('.bst-header__nav-link')?.focus();
        }
      });

      this.navItems.forEach((item) => {
        const link = item.querySelector('.bst-header__nav-link');
        const handle = item.dataset.navHandle;
        const panel = this.header.querySelector(`.bst-header__mega-panel[data-nav-handle="${handle}"]`);
        if (!link || !panel) return;

        link.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            if (item.classList.contains(OPEN_CLASS)) {
              this._closePanel(item, panel);
            } else {
              this._openPanel(item, panel);
              panel.querySelector('a')?.focus();
            }
          }
        });
      });

      this.dropdownItems.forEach((item) => {
        const link = item.querySelector('.bst-header__nav-link');
        const dropdown = item.querySelector('.bst-header__dropdown');
        if (!link || !dropdown) return;

        link.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            if (item.classList.contains(OPEN_CLASS)) {
              this._closeDropdown(item);
            } else {
              this._openDropdown(item);
              dropdown.querySelector('a')?.focus();
            }
          }
        });
      });
    }

    _bindResize() {
      DESKTOP_MQ.addEventListener('change', (e) => {
        if (e.matches) {
          this._closeMobile();
          this._resetNavIndicator();
        } else if (this.indicator) {
          this.indicator.hidden = true;
        }
      });

      window.addEventListener('resize', () => {
        if (this._activeDropdown) {
          this._positionDropdown(this._activeDropdown);
        }
      });
    }

  }

  function init() {
    document.querySelectorAll('.bst-header').forEach((el) => {
      if (!el._bstHeaderInit) {
        el._bstHeaderInit = new BstHeader(el);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
